"use client";

import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

type PurchaseReviewDialogProps = {
  open: boolean;
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function PurchaseReviewDialog({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  dismissible = true,
}: PurchaseReviewDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.dataset.purchaseFlowActive = "true";
    window.dispatchEvent(new CustomEvent("sa:purchase-flow", { detail: { active: true } }));
    const backgroundElements = Array.from(document.body.children).filter(
      (element) => !element.hasAttribute("data-purchase-dialog-root"),
    ) as HTMLElement[];
    const backgroundState = backgroundElements.map((element) => ({
      element,
      ariaHidden: element.getAttribute("aria-hidden"),
      inert: element.inert,
    }));
    backgroundElements.forEach((element) => {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    });

    const focusTimer = window.setTimeout(() => {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? dialogRef.current)?.focus();
    }, 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        if (dismissible) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      delete document.body.dataset.purchaseFlowActive;
      backgroundState.forEach(({ element, ariaHidden, inert }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
      window.dispatchEvent(new CustomEvent("sa:purchase-flow", { detail: { active: false } }));
      window.setTimeout(() => returnFocusRef.current?.focus(), 0);
    };
  }, [dismissible, open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div data-purchase-dialog-root className="fixed inset-0 z-[120] flex items-end justify-center bg-[#10211e]/78 backdrop-blur-sm sm:items-center sm:p-5" role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className="flex max-h-[100dvh] min-h-[min(38rem,100dvh)] w-full flex-col overflow-hidden rounded-t-[24px] border border-[#d9c78f]/45 bg-[#fffaf2] text-[#13211f] shadow-[0_30px_100px_rgba(0,0,0,0.35)] outline-none [color-scheme:light] sm:max-h-[min(90dvh,54rem)] sm:min-h-0 sm:max-w-2xl sm:rounded-[26px]"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[#e2d3bc] px-5 py-4 sm:px-6">
          <div>
            <h2 id={titleId} className="font-serif text-2xl font-semibold leading-tight text-[#123b34] sm:text-3xl">{title}</h2>
            <p id={descriptionId} className="mt-1 text-sm leading-5 text-[#61706b]">{description}</p>
          </div>
          {dismissible ? <button type="button" onClick={onClose} aria-label="Close purchase dialog" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d8cab6] bg-white text-lg font-bold text-[#13211f] transition hover:border-[#b7892d]">×</button> : null}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
        {footer ? <footer className="sticky bottom-0 shrink-0 border-t border-[#e2d3bc] bg-[#fffaf2]/95 px-5 py-4 backdrop-blur sm:px-6">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
